import { useMemo } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Box, Button, Stack } from '@mui/material';
import { useOutletContext } from 'react-router-dom';
import { Organization } from '@/types/organizations';
import { useAccessControl, withAccessControl } from '@/features/access-control';
import { OrganizationContext } from '@/features/organizations/components/OrganizationLayout';
import PageHeader from '@/components/PageHeader';
import useSnackbar from '@/hooks/useSnackbar';
import ColorSetting from './components/ColorSetting';
import Section from './components/Section';
import FileSetting from './components/FileSetting';
import SwitchSetting from './components/SwitchSetting';
import { useUpdateOrganizationSettingsMutation } from '@/features/organizations/api/organizationsApi';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  ConciergeBell,
  HandPlatter,
  SwatchBook,
} from 'lucide-react';
import { pick } from 'lodash';
import SelectSetting from './components/SelectSetting';
import Whatsapp from '@/components/SocialMediaIcon/assets/Whatsapp';

const SETTINGS_FIELDS = [
  'settings',
  'logo',
  'isotype',
  'header',
  'primaryColor',
  'secondaryColor',
];

export type OrganizationSettingsForm = Pick<
  Organization,
  'settings' | 'logo' | 'isotype' | 'header' | 'primaryColor' | 'secondaryColor'
>;

const SettingsSchema = Yup.object().shape({
  services: Yup.object().shape({
    delivery: Yup.object().shape({
      isEnabled: Yup.boolean(),
    }),
    orderManagement: Yup.string(),
  }),
  includeTax: Yup.boolean(),
});

const Settings = () => {
  const [openSnackbar] = useSnackbar();
  const { hasAccess } = useAccessControl();
  const { currentOrganization } = useOutletContext<OrganizationContext>();
  const [updateSettings, { isLoading }] =
    useUpdateOrganizationSettingsMutation();

  // const [confirmationOrgDialog, confirmOrganizationDeletion] = useConfirmation({
  //   title: 'Eliminar',
  //   message: 'Deseas eliminar esta organización?',
  //   dispatcher: deleteOrganization,
  // });

  const handleDeleteOrg = (id: string) => {
    // confirmOrganizationDeletion({ organizationId: id });
  };

  const handleSubmit = async (values: OrganizationSettingsForm) => {
    const response = await updateSettings({
      organizationId: currentOrganization?.id,
      payload: values,
    });

    if (response?.error) {
      openSnackbar('Error al guardar las configuraciones', 'error');
    } else {
      openSnackbar('Configuraciones guardadas', 'success');
    }
  };

  const initialState = useMemo(() => {
    let draft = {};

    if (currentOrganization) {
      draft = pick(currentOrganization, SETTINGS_FIELDS);
    }

    return draft as OrganizationSettingsForm;
  }, [currentOrganization]);

  if (!currentOrganization) return null;

  return (
    <Formik
      enableReinitialize
      initialValues={initialState}
      validationSchema={SettingsSchema}
      onSubmit={handleSubmit}
    >
      <Form>
        <Stack spacing={4}>
          <PageHeader
            title="Configuraciones"
            action={
              hasAccess('update.organization') ? (
                <Button size="large" color="success" type="submit">
                  Guardar
                </Button>
              ) : undefined
            }
          />
          <Stack alignItems="center">
            <Box sx={{ maxWidth: 800, width: '100%' }} mb={3}>
              <Stack spacing={4}>
                <Section title="Servicios" icon={<HandPlatter />}>
                  <SwitchSetting
                    title="Delivery"
                    description="Habilita/deshabilita el servicio de delivery"
                    field="settings.services.delivery.isEnabled"
                  />
                </Section>

                <Section title="Checkout" icon={<HandPlatter />}>
                  <SwitchSetting
                    title="Impuesto IVA"
                    description="Habilita/deshabilita el impuesto de IVA"
                    field="settings.includeTax"
                  />
                </Section>

                <Section title="Personalización" icon={<SwatchBook />}>
                  <Stack gap={2}>
                    <ColorSetting
                      field="primaryColor"
                      title="Color primario"
                      description="Color principal, se utiliza en botones y títulos"
                    />
                    <ColorSetting
                      field="secondaryColor"
                      title="Color secundario"
                      description="Se utiliza como complemento, en fondos y botones secundarios"
                    />
                  </Stack>
                </Section>

                <Section title="Lobby" icon={<ConciergeBell />}>
                  <Stack gap={2}>
                    <FileSetting
                      field="header"
                      title="Banner Principal"
                      description="Imagen utilizada de banner junto al logotipo"
                    />
                    <FileSetting field="logo" title="Logotipo" description="Combinación de palabra/letras y símbolo que representen la marca" />
                    <FileSetting field="isotype" title="Isotipo" description="Símbolo que representen la marca" />
                  </Stack>
                </Section>

                <Section title="Pedidos" icon={<HandPlatter />}>
                  <Stack gap={2}>
                    <SelectSetting
                      field={'settings.orderManagement'}
                      title={'Administración'}
                      description={
                        'Selecciona el método para recibir y gestionar tus pedidos'
                      }
                      options={[
                        {
                          label: 'Whatsapp',
                          value: 'whatsapp',
                          children: (
                            <Whatsapp
                              color="#25D366"
                              variant="outlined"
                              strokeWidth={2}
                            />
                          ),
                        },
                        { label: 'bitelink', value: 'system' },
                      ]}
                    />
                  </Stack>
                </Section>

                <Stack spacing={3} direction="row" justifyContent="center">
                  {hasAccess('delete.organization') ? (
                    <LoadingButton
                      variant="contained"
                      loading={isLoading}
                      color="error"
                      onClick={() => handleDeleteOrg(currentOrganization.id)}
                    >
                      Eliminar Comercio
                    </LoadingButton>
                  ) : null}
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </Stack>
      </Form>
    </Formik>
  );
};

export default withAccessControl(Settings, 'update.organization');
